import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, ImageOff } from 'lucide-react';
import api, { errMsg, fileUrl } from '@/lib/api';
import { ImageDrop } from '@/components/ImageDrop';
import { useI18n } from '@/context/LanguageContext';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

export const Step2Photos = ({ usePhoto, setUsePhoto, photos, selectedPhotoId, setSelectedPhotoId, reloadPhotos }) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);

  const upload = async (files) => {
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        await api.post('/photos', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      await reloadPhotos();
      toast.success(t('step2.photosAdded'));
    } catch (e) {
      toast.error(errMsg(e, t('step2.photoUploadFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-lg font-semibold">{t('step2.title')}</h2>
        <p className="mt-1.5 text-sm text-[hsl(var(--text-muted))]">{t('step2.desc')}</p>
      </div>

      <div className="flex items-center justify-between rounded-[var(--radius-lg)] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))] px-4 py-3.5">
        <Label htmlFor="use-photo" className="cursor-pointer text-sm font-medium">
          {t('step2.switchLabel')}
        </Label>
        <Switch
          id="use-photo"
          data-testid="use-photo-switch"
          checked={usePhoto}
          onCheckedChange={(v) => {
            setUsePhoto(v);
            if (!v) setSelectedPhotoId(null);
          }}
        />
      </div>

      {usePhoto && (
        <div className="space-y-4">
          {photos.length === 0 ? (
            <div
              data-testid="empty-state"
              className="rounded-[var(--radius-lg)] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] px-5 py-8 text-center"
            >
              <ImageOff className="mx-auto h-8 w-8 text-[hsl(var(--text-muted))]" />
              <p className="mt-3 text-sm font-medium">{t('step2.emptyTitle')}</p>
              <p className="mt-1 text-xs text-[hsl(var(--text-muted))]">
                {t('step2.emptyDesc1')}{' '}
                <Link to="/hesabim" className="font-semibold text-[hsl(var(--brand-red))] hover:underline">
                  {t('step2.emptyLink')}
                </Link>{' '}
                {t('step2.emptyDesc2')}
              </p>
            </div>
          ) : (
            <div data-testid="my-photos-grid" className="grid grid-cols-3 gap-3 sm:grid-cols-4">
              {photos.map((p) => {
                const sel = selectedPhotoId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`my-photo-item-${p.id}`}
                    onClick={() => setSelectedPhotoId(sel ? null : p.id)}
                    className={[
                      'group relative overflow-hidden rounded-[12px] border bg-[hsl(var(--surface))] transition-colors duration-150',
                      sel
                        ? 'border-[hsl(var(--brand-red))] ring-2 ring-[hsl(var(--brand-red))]/40'
                        : 'border-[hsl(var(--border-soft))] hover:border-[hsl(var(--border-strong))]',
                    ].join(' ')}
                    style={{ aspectRatio: '1' }}
                  >
                    <img src={fileUrl(p.url)} alt={p.name} className="h-full w-full object-cover" />
                    {sel && (
                      <span className="absolute right-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-full bg-[hsl(var(--brand-red))] text-white">
                        <Check className="h-3.5 w-3.5" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <ImageDrop
            testId="step2-photo-upload"
            compact
            multiple
            enablePaste={false}
            onFiles={upload}
            busy={busy}
            title={t('step2.uploadTitle')}
            hint={t('step2.uploadHint')}
          />
        </div>
      )}

      {!usePhoto && (
        <p className="text-sm text-[hsl(var(--text-muted))]">{t('step2.skip')}</p>
      )}
    </div>
  );
};
